import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Check, ChevronDown, Loader2, Mic } from 'lucide-react';
import { useRef, useState } from 'react';
import type { AudioInputDevice, ComposerDictation } from '../useVoiceSession';

interface ChatComposerMicButtonProps {
  dictation: ComposerDictation;
  disabled: boolean;
}

// Must match the w-72 class on DropdownMenuContent; needed before render to centre
// the menu on the panel (Radix ignores alignOffset for align="center", so we use
// align="end" and offset from the trigger's right edge).
const MENU_WIDTH_PX = 288;

export function ChatComposerMicButton({ dictation, disabled }: ChatComposerMicButtonProps) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [devices, setDevices] = useState<AudioInputDevice[]>([]);
  const [menuAlignOffset, setMenuAlignOffset] = useState(0);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      const trigger = triggerRef.current;
      const panel = trigger?.closest<HTMLElement>('[data-testid="speak2me-panel"]');
      if (trigger && panel) {
        const triggerRect = trigger.getBoundingClientRect();
        const panelRect = panel.getBoundingClientRect();
        const desiredRightEdge = panelRect.left + panelRect.width / 2 + MENU_WIDTH_PX / 2;
        setMenuAlignOffset(Math.round(triggerRect.right - desiredRightEdge));
      }
      void dictation.listDevices().then(setDevices);
    }
    setMenuOpen(open);
  };

  const label = dictation.starting
    ? 'Starting dictation...'
    : dictation.supported
      ? 'Dictate'
      : 'Dictation unavailable';

  return (
    <span
      className={`group/mic inline-flex items-center rounded-md transition-colors duration-150 ${
        menuOpen ? 'bg-muted' : 'hover:bg-muted'
      }`}
    >
      <DropdownMenu modal={false} open={menuOpen} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button
            ref={triggerRef}
            data-testid="chat-composer-dictate-menu"
            type="button"
            variant="ghost"
            size="icon"
            className={`h-7 transition-all duration-150 hover:bg-transparent ${
              menuOpen
                ? 'w-5 opacity-100'
                : 'w-0 overflow-hidden p-0 opacity-0 group-hover/mic:w-5 group-hover/mic:opacity-100'
            }`}
            disabled={!dictation.supported || dictation.starting || disabled}
            title="Choose microphone"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" alignOffset={menuAlignOffset} className="w-72">
          <DropdownMenuItem
            onClick={() => dictation.selectDevice(null)}
            className="gap-2"
          >
            <Check
              className={`h-3.5 w-3.5 shrink-0 ${
                dictation.selectedDeviceId === null ? 'opacity-100' : 'opacity-0'
              }`}
            />
            <span className="truncate">Default microphone</span>
          </DropdownMenuItem>
          {devices.map((device) => (
            <DropdownMenuItem
              key={device.deviceId}
              onClick={() => dictation.selectDevice(device.deviceId)}
              className="gap-2"
            >
              <Check
                className={`h-3.5 w-3.5 shrink-0 ${
                  dictation.selectedDeviceId === device.deviceId ? 'opacity-100' : 'opacity-0'
                }`}
              />
              <span className="truncate">{device.label}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">
            <Button
              data-testid="chat-composer-dictate"
              type="button"
              variant="ghost"
              size="icon"
              className={`h-7 w-7 transition-colors duration-150 ${
                menuOpen
                  ? 'bg-muted-foreground/15'
                  : 'group-hover/mic:bg-muted-foreground/15'
              } hover:bg-muted-foreground/20`}
              disabled={!dictation.supported || dictation.starting || disabled}
              onClick={() => {
                setMenuOpen(false);
                dictation.start();
              }}
              title={label}
            >
              {dictation.starting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Mic className="h-3.5 w-3.5" />
              )}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent className="z-[60] mr-2" sideOffset={4}>
          <p>{label}</p>
        </TooltipContent>
      </Tooltip>
    </span>
  );
}
